// Das Satz-Menü (task-016, docs/architektur-paket-2.md, Abschnitt „Client“, `hud/`): T öffnet es, eine
// Ziffer von 1 bis 6 oder ein Klick wählt einen der festen Sätze, T oder Escape schließt. Freien Text
// gibt es nicht: Es gibt kein Eingabefeld, und hinaus geht nur die Nummer – den Text setzt der Server ein.
//
// Solange das Menü offen ist, ist die Maus frei (main.ts), damit man auch klicken kann; die Tasten des
// Spiels kommen dann nicht an, darum hört das Menü selbst zu. Ein wiederholtes keydown (gehaltene Taste)
// wählt und schließt nie – wie überall in der Eingabe.
import { PHRASES, isPhraseIndex, type PhraseIndex } from '@bauernhof/shared';
import { KEYS } from '../input/keys.js';

/** Der Satz zu einer Taste (`KeyboardEvent.code`): Ziffern 1 bis 6, oben oder im Ziffernblock. */
export function satzFuerTaste(code: string): PhraseIndex | null {
  const treffer = /^(?:Digit|Numpad)(\d)$/.exec(code);
  if (treffer === null) return null;
  const index = Number(treffer[1]) - 1;
  return isPhraseIndex(index) ? index : null;
}

/** Eine Zeile des Menüs: Satz, Ziffer und Text. */
export interface SatzZeile {
  satz: PhraseIndex;
  taste: string;
  text: string;
}

/** Die Zeilen des Menüs, in der Reihenfolge von `PHRASES`. */
export function satzZeilen(): SatzZeile[] {
  const zeilen: SatzZeile[] = [];
  PHRASES.forEach((text, index) => {
    if (isPhraseIndex(index)) zeilen.push({ satz: index, taste: String(index + 1), text });
  });
  return zeilen;
}

export interface SatzmenueOptionen {
  /** Ein Satz ist gewählt; das Menü ist dann schon zu. */
  beiWahl(satz: PhraseIndex): void;
  /** T, Escape oder „Schließen“ ohne Wahl. */
  beiSchliessen(): void;
}

export interface Satzmenue {
  oeffne(): void;
  /** Schließt ohne Rückruf. */
  schliesse(): void;
  istOffen(): boolean;
  dispose(): void;
}

export function createSatzmenue(parent: HTMLElement, optionen: SatzmenueOptionen): Satzmenue {
  const wurzel = document.createElement('div');
  wurzel.className = 'satzmenue';
  wurzel.hidden = true;

  const karte = document.createElement('div');
  karte.className = 'satzmenue-karte';
  karte.setAttribute('role', 'dialog');
  karte.setAttribute('aria-label', 'Was möchtest du sagen?');

  const titel = document.createElement('h1');
  titel.textContent = 'Was möchtest du sagen?';

  const saetze = document.createElement('div');
  saetze.className = 'satzmenue-saetze';
  const knoepfe = satzZeilen().map((zeile) => {
    const knopf = document.createElement('button');
    knopf.type = 'button';
    knopf.className = 'satzmenue-knopf';
    const taste = document.createElement('span');
    taste.className = 'satzmenue-taste';
    taste.textContent = zeile.taste;
    const text = document.createElement('span');
    text.textContent = zeile.text;
    knopf.append(taste, text);
    knopf.addEventListener('click', () => waehle(zeile.satz));
    saetze.append(knopf);
    return knopf;
  });

  const anleitung = document.createElement('p');
  anleitung.className = 'satzmenue-anleitung';
  anleitung.textContent = 'Drücke eine Ziffer oder klicke auf einen Satz. T oder Escape schließt.';

  const schliessen = document.createElement('button');
  schliessen.type = 'button';
  schliessen.className = 'satzmenue-schliessen';
  schliessen.textContent = 'Schließen';
  schliessen.addEventListener('click', () => zu());

  karte.append(titel, saetze, anleitung, schliessen);
  wurzel.append(karte);
  parent.append(wurzel);

  function waehle(satz: PhraseIndex): void {
    if (wurzel.hidden) return;
    wurzel.hidden = true;
    optionen.beiWahl(satz);
  }

  function zu(): void {
    if (wurzel.hidden) return;
    wurzel.hidden = true;
    optionen.beiSchliessen();
  }

  const beiTaste = (ereignis: KeyboardEvent): void => {
    if (wurzel.hidden || ereignis.repeat) return;
    const satz = satzFuerTaste(ereignis.code);
    if (satz === null && ereignis.code !== KEYS.saetze && ereignis.code !== 'Escape') return;
    ereignis.preventDefault();
    ereignis.stopPropagation();
    if (satz === null) zu();
    else waehle(satz);
  };
  window.addEventListener('keydown', beiTaste);

  return {
    oeffne() {
      wurzel.hidden = false;
      // Mit Tab und Enter geht es auch ohne Maus; der erste Satz hat den Fokus.
      knoepfe[0]?.focus();
    },
    schliesse() {
      wurzel.hidden = true;
    },
    istOffen: () => !wurzel.hidden,
    dispose() {
      window.removeEventListener('keydown', beiTaste);
      wurzel.remove();
    },
  };
}
